import type { Context } from '@/overmind';

import type { FunctionalGrid } from '../calculation/grid/FunctionalGrid';

export function getFeaturesOnCalculationTacticResult(
  overmindInstance: Context
) {
  overmindInstance.reaction(
    ({ arcgis, calculation: calculationV2 }) =>
      [
        calculationV2.gridVersionName,
        arcgis.config.value,
        calculationV2.gridElementsDetailsByCalculationType,
        calculationV2.calculationTactic,
        arcgis.featureGeometries.current,
        calculationV2.calculationContext
      ] as const,
    handleOnCalculationTacticResultStateChanged(
      overmindInstance.actions.arcgis.featureGeometries.getFeatures
    )
  );
}
type FunctionalGridElementTypesRepresentedInArcGisUn = keyof Omit<
  FunctionalGrid,
  'loadInputPoints'
>;
type FunctionalGridElementRepresentedInArcGisUn =
  FunctionalGrid[FunctionalGridElementTypesRepresentedInArcGisUn][number];
export const handleOnCalculationTacticResultStateChanged =
  (
    getFeatures: Context['actions']['arcgis']['featureGeometries']['getFeatures']
  ) =>
  async ([
    gridVersionName,
    configValue,
    evaluatedElements,
    calculationTactic,
    featureGeometries,
    calculationContext
  ]: readonly [
    Context['state']['calculation']['gridVersionName'],
    Context['state']['arcgis']['config']['value'],
    Context['state']['calculation']['gridElementsDetailsByCalculationType'],
    Context['state']['calculation']['calculationTactic'],
    Context['state']['arcgis']['featureGeometries']['current'],
    Context['state']['calculation']['calculationContext']
  ]) => {
    if (featureGeometries === 'LOADED' || featureGeometries === 'LOADING' || calculationContext.type === 'NOT_AVAILABLE')
      return;
    if (
      calculationTactic.current !== 'LOADED' ||
      (calculationTactic.current === 'LOADED' &&
        calculationTactic.value.status !== 'Completed')
    )
      return;
    if (!configValue || evaluatedElements.type === 'NO_EVALUATION') return;

    const functionalGridTypesToInclude: FunctionalGridElementTypesRepresentedInArcGisUn[] =
      [
        'acLineSegments',
        'busbars',
        'energyConsumers',
        'externalNetworkInjections',
        'powerTransformerEnds',
        'powerTransformers',
        'switches',
        'terminals'
      ];

    const filteredIds =
      calculationTactic.value.status === 'Completed'
        ? getFeatureIdsForFunctionalGridTypes(
            calculationTactic.value.calculationSetResult.grid.functional,
            functionalGridTypesToInclude
          )
        : [];

    const layerIdsToSearch = configValue.mapSource.layerIds;
    const featureIdsIncludingInitiatingGridElement = filteredIds.concat(calculationContext.calculationContext.relatedObjectId)
    await getFeatures({
      featureIds: featureIdsIncludingInitiatingGridElement,
      layerIdsToSearch,
      gridVersionName
    });
  };
function getFeatureIdsForFunctionalGridTypes(
  functionalGrid: FunctionalGrid,
  functionalGridTypesToInclude: FunctionalGridElementTypesRepresentedInArcGisUn[]
) {
  return Object.entries(functionalGrid)
    .filter(([key]) =>
      functionalGridTypesToInclude.includes(
        key as unknown as FunctionalGridElementTypesRepresentedInArcGisUn
      )
    )
    .map(([_, elements]) => elements)
    .flat(1)
    .map(
      element =>
        (element as unknown as FunctionalGridElementRepresentedInArcGisUn)
          .objectId
    );
}
